import { objectKeys } from "@toss/utils";
import { AxiosError } from "axios";
import { isObject } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { BuildingModel } from "src/api/building/building-types";
import { ContractManageList, ContractStep } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import {
  createProductAsync,
  getInternalMemberContractMapsAsync,
  postInternalMemberContractMapsAsync,
  productStatusAsync,
  updateProductAsync,
} from "src/api/product/product-api";
import { ProductAddModel, ProductEditModel, ProductModel } from "src/api/product/product-types";
import { getProviderList, providerDetailAsync } from "src/api/provider/provider-api";
import { ProviderPayletterModel, ProviderPopbillModel } from "src/api/provider/provider-types";
import { BaseButton, BaseCheckbox, BaseInput, BaseModal, BaseRadio, BaseTooltip } from "src/components";
import EditCancelButton from "src/components/EditCancelButton";
import { FullDatePicker } from "src/components/FullDatePicker";
import Link from "src/components/PartnerLink";
import SelectProviderPopup from "src/components/selectPopup/SelectProviderPopup";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import SearchContractPopup from "src/pages/commonPopup/SearchContractPopup";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";
import { globalPartnerState } from "src/recoil/partners/atom";
import { useToast } from "src/recoil/toast/hook";
import { isString, YmdFormat } from "src/utils";
import { getProductDetailPath } from "src/utils/route-util";
import BuildingFloorHoModal from "../../components/BuildingFloorHoModal";
import ProductBuildingListModal from "../../components/ProductBuildingListModal";
import BuildingAndHoTable from "../../modalPopup/table/BuildingAndHoTable";
import {
  BuildingFloorAndHo,
  changePgcodeLabel,
  Modal,
  ProductStatus,
  productStatusOptions,
  ProductStatusT,
  ProductTypeLabels,
  ProductTypes,
  salePeriodTypeOptions,
} from "../../product-types";
import { changeBuildings, getErrorData, getProviderName, isPrimaryBuildingChecked, makeBuilding, onDrawViewTable, ProductErrorDataMap } from "./libs";

export type ProductFormData = {
  providerId: ProductAddModel["providerId"];
  productName: ProductAddModel["productName"];
  introduce: ProductAddModel["introduce"];
  buildingList: Array<BuildingFloorAndHo>;
  productType: ProductAddModel["productType"];
  status?: ProductAddModel["status"];
  /** 내부 이용자 공개 (일부 계약) 선택 시 노출 대상 계약 목록 */
  memberContractList?: ContractManageList[];
  isContractAvail: boolean;
  isMemberCancelable: boolean;
  memberCancelableMinutes?: number;
  paymentDueMinutes?: number;
  saleStartDate: string;
  saleEndDate: string;
  salePeriodType: ProductModel["salePeriodType"];
  approvalType: ProductModel["approvalType"];
  isUseApplication?: boolean;
  isOnlinePayment?: boolean;
  pgCodeOption: {
    items: Array<{
      pgcode: string;
      isEnabled: boolean;
      priority: number;
    }>;
  };
};

type Props = {
  product?: ProductModel;
  productId?: string;
};

/* 
  공간상품 등록 > 등록 or 수정 > 기본 정보
 */
const BasicInfoForm = ({ product: productDetail, productId }: Props) => {
  const { openToast } = useToast();
  const { openErrorModal } = useErrorModal();
  const navigate = useNavigate();
  const { partnerId } = useParams();

  const { setBaseModal, setAbstractModalZ1 } = useModal();
  const { isAuthority } = usePartnerAuthority();

  const partner = useRecoilValue(globalPartnerState);
  /** 파트너의 S2 타입 유형 활성화 여부  */
  const isActiveTimeCourt = partner?.features?.filter((PaPartner) => PaPartner.name === "CTRLROOM_PRODUCT_S2")?.[0]?.active;
  /** 파트너의 공개/비공개/링크공개 라디오 버튼 활성화 여부  */
  const isActiveProductPublic = partner?.features?.filter((PaPartner) => PaPartner.name === "CTRLROOM_PRODUCT_PUBLIC")?.[0]?.active;

  const isEdit = useMemo(() => isString(productId), [productId]);
  const taapSpaceUrl = useMemo(() => process.env.REACT_APP_COURT_BASSEURL, []);
  //상품 링크 주소
  const t_product_url = taapSpaceUrl + "/front/court/product/" + productId;

  const [selectedBuilding, setSelectedBuilding] = useState<BuildingModel | null>(null); // 모달에서 선택한 빌딩
  const [selectedRow, setSelectedRow] = useState({});
  const [buildingAndHo, setBuildingAndHo] = useState<Array<BuildingFloorAndHo>>([]); // 테이블용
  const [submitData, setSubmitData] = useState<ProductFormData>();

  const [buildingModal, setBuildingModal] = useState<Modal>({ isOpen: false }); //빌딩 팝업 모달
  const [floorHoModal, setFloorHoModal] = useState<Modal>({ isOpen: false });
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });

  const [confirmModal, setConfirmModal] = useState<Modal>({
    isOpen: false,
  });
  const [cancelModal, setCancelModal] = useState<Modal>({
    isOpen: false,
  });
  const [productStatusModal, setProductStatusModal] = useState<Modal>({
    isOpen: false,
  });
  const [isPgcodeOptionCheckModal, setIsPgcodeOptionCheckModal] = useState<Modal>({
    isOpen: false,
  });
  // 내부 이용자 공개(일부 계약) 계약 선택 팝업
  const [isContractPopupOpen, setIsContractPopupOpen] = useState(false);
  // 저장 시 삭제(isDeleted) 대상 계산을 위해 최초 로드된 매핑 계약을 보관
  const originalMemberContractsRef = useRef<ContractManageList[]>([]);

  const [selectedProvider, setSelectedProvider] = useState<{
    providerId: string;
    uid: string;
    providerName: string;
  }>();

  //프로바이더 정산정보 유효성 체크를 위한 프로바이더 상세 정보
  const [providerNormalCardApi, setProviderNormalCardApi] = useState<ProviderPayletterModel>();
  const [providerPopbillApi, setProviderPopbillApi] = useState<ProviderPopbillModel>();

  // 취소 가능 시간 로컬 상태 (화면 표시용)
  const [offsetDays, setOffsetDays] = useState(0);
  const [offsetHours, setOffsetHours] = useState(24);
  const [offsetMinutes, setOffsetMinutes] = useState(0);

  // 결제기한 로컬 상태 (화면 표시용)
  const [paymentDueDays, setPaymentDueDays] = useState(0);
  const [paymentDueHours, setPaymentDueHours] = useState(0);
  const [paymentDueMinutesState, setPaymentDueMinutesState] = useState(0);

  // 마지막으로 수정된 필드 추적
  const lastChangedFieldRef = useRef<"days" | "hours" | "minutes" | null>(null);
  const lastChangedPaymentDueFieldRef = useRef<"days" | "hours" | "minutes" | null>(null);

  // 공간상품 등록 Api
  const { executeAsync: createProduct } = useApiOperation(createProductAsync);
  //
  // 공간상품 수정 Api
  const { executeAsync: updateProduct } = useApiOperation(updateProductAsync);

  // 내부 이용자 공개(일부 계약) 대상 계약 조회/저장 Api (RPP25/RPP26)
  const { executeAsync: getInternalMemberContractMaps } = useApiOperation(getInternalMemberContractMapsAsync);
  const { executeAsync: postInternalMemberContractMaps } = useApiOperation(postInternalMemberContractMapsAsync);

  //  프로바이더 목록 api
  const { executeAsync: getProviers } = useApiOperation(getProviderList);

  const { executeAsync: getProviderDetail } = useApiOperation(providerDetailAsync);

  // 공개 가능 여부 검사

  const { executeAsync: getProductStatus } = useApiOperation(productStatusAsync, { noErrorModal: true });

  const fetchProviderDetail = useCallback(
    async (providerId: string) => {
      const providerDetail = await getProviderDetail({ providerId: providerId });

      if (providerDetail && providerDetail.data && providerDetail.data.data && providerDetail.data.data.payletterList) {
        setProviderNormalCardApi(providerDetail.data.data.payletterList.find((item: ProviderPayletterModel) => item.clientType === "NORMAL"));
        setProviderPopbillApi(providerDetail.data.data.provider);
      }
    },
    [getProviderDetail],
  );

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      providerId: "",
      productType: "FULL_COURT",
      productName: "",
      introduce: "",
      buildingList: [],
      status: "DISABLED",
      memberContractList: [],
      memberCancelableMinutes: 1440, // 24시간 = 1440분
      paymentDueMinutes: 5,
    },
    mode: "onChange",
  });

  /** 수정모드에서 디폴트 벨류 초기화, 기본 정보 셋팅 */
  useEffect(() => {
    if (isEdit && productDetail?.buildingList) {
      const newBuilding = makeBuilding(productDetail["buildingList"]);

      const totalMinutes = productDetail?.memberCancelableMinutes ?? 1440;
      // 분을 일/시/분으로 변환
      const days = Math.floor(totalMinutes / 1440);
      const hours = Math.floor((totalMinutes % 1440) / 60);
      const minutes = totalMinutes % 60;

      setOffsetDays(days);
      setOffsetHours(hours);
      setOffsetMinutes(minutes);

      // paymentDueMinutes 초기화
      const paymentDueTotalMinutes = (productDetail as any)?.paymentDueMinutes ?? 5;
      const paymentDueDays = Math.floor(paymentDueTotalMinutes / 1440);
      const paymentDueHours = Math.floor((paymentDueTotalMinutes % 1440) / 60);
      const paymentDueMins = paymentDueTotalMinutes % 60;

      setPaymentDueDays(paymentDueDays);
      setPaymentDueHours(paymentDueHours);
      setPaymentDueMinutesState(paymentDueMins);

      reset({
        providerId: productDetail?.providerId ?? "",
        productType: productDetail?.productType ?? "FULL_COURT",
        productName: productDetail?.productName ?? "",
        introduce: productDetail?.introduce ?? "",
        buildingList: productDetail?.buildingList ? newBuilding : [],
        status: (productDetail?.status as ProductAddModel["status"]) ?? "DISABLED",
        // 내부 이용자 공개(일부 계약) 매핑은 RPP25로 별도 조회 (아래 setValue)
        memberContractList: [],
        saleStartDate: productDetail?.saleStartDate ?? "",
        saleEndDate: productDetail?.saleEndDate ?? "",
        salePeriodType: productDetail?.salePeriodType ?? "ALWAYS",
        approvalType: productDetail?.approvalType ?? "AUTO",
        pgCodeOption: productDetail?.pgcodeOption ?? { items: [] },
        isMemberCancelable: productDetail?.isMemberCancelable ?? false,
        memberCancelableMinutes: totalMinutes,
        paymentDueMinutes: paymentDueTotalMinutes,
        isOnlinePayment: productDetail?.isOnlinePayment ?? true,
      });

      setBuildingAndHo(newBuilding);
      isPrimaryBuildingChecked(newBuilding, setSelectedRow, productId);

      // 내부 이용자 공개(일부 계약) 대상 계약 매핑 조회 후 초기화 (RPP25)
      if (productId) {
        getInternalMemberContractMaps({ productId: +productId }).then((res) => {
          const maps = res?.data?.data?.allowedContractMap ?? [];
          const contracts = maps
            .filter((map) => !map.isDeleted)
            .map((map) => ({ contractManageId: Number(map.contractManageId), contractId: Number(map.contractId) } as ContractManageList));
          originalMemberContractsRef.current = contracts;
          setValue("memberContractList", contracts);
        });
      }
    }
  }, [productDetail, isEdit]);

  /** 수정모드에서 프로바이더명 조회 후 setState */
  useEffect(() => {
    if (isEdit && productDetail?.providerId) {
      getProviderName(productDetail.providerId, getProviers).then((data) => setSelectedProvider(data));
      fetchProviderDetail(String(productDetail.providerId));
    }
  }, [productDetail, isEdit]);

  /** memberCancelableMinutes validation */
  const isCancelable = watch("isMemberCancelable");

  useEffect(() => {
    if (isCancelable === true) {
      const lastChanged = lastChangedFieldRef.current;

      // 마지막으로 변경된 필드의 에러를 우선 체크
      if (lastChanged === "hours" && offsetHours > 23) {
        setError("memberCancelableMinutes", {
          type: "manual",
          message: "23시간까지 입력 가능합니다",
        });
        return;
      }

      if (lastChanged === "minutes" && offsetMinutes > 59) {
        setError("memberCancelableMinutes", {
          type: "manual",
          message: "59분까지 입력 가능합니다",
        });
        return;
      }

      // 그 외의 경우 순차적으로 체크
      if (offsetHours > 23) {
        setError("memberCancelableMinutes", {
          type: "manual",
          message: "시간은 23까지 입력할 수 있습니다.",
        });
        return;
      }

      // 총 분 계산 및 최소 10분 체크
      const totalMinutes = offsetDays * 1440 + offsetHours * 60 + offsetMinutes;
      if (totalMinutes < 10) {
        setError("memberCancelableMinutes", {
          type: "manual",
          message: "10분 이상의 시간을 입력해주세요. (최소값 10분)",
        });
        return;
      }

      // validation 통과하면 총 분을 form에 저장
      setValue("memberCancelableMinutes", totalMinutes);
      clearErrors("memberCancelableMinutes");
    } else {
      clearErrors("memberCancelableMinutes");
    }
  }, [isCancelable, offsetDays, offsetHours, offsetMinutes, setError, clearErrors, setValue]);

  /** paymentDueMinutes validation */
  useEffect(() => {
    // 신규 등록(id가 없는 경우)에서는 검증하지 않음
    if (!isEdit) {
      return;
    }

    const lastChanged = lastChangedPaymentDueFieldRef.current;

    // 마지막으로 변경된 필드의 에러를 우선 체크
    if (lastChanged === "hours" && paymentDueHours > 23) {
      setError("paymentDueMinutes", {
        type: "manual",
        message: "23시간까지 입력 가능합니다",
      });
      return;
    }

    if (lastChanged === "minutes" && paymentDueMinutesState > 59) {
      setError("paymentDueMinutes", {
        type: "manual",
        message: "59분까지 입력 가능합니다",
      });
      return;
    }

    // 그 외의 경우 순차적으로 체크
    if (paymentDueHours > 23) {
      setError("paymentDueMinutes", {
        type: "manual",
        message: "시간은 23까지 입력할 수 있습니다.",
      });
      return;
    }

    if (paymentDueMinutesState > 59) {
      setError("paymentDueMinutes", {
        type: "manual",
        message: "분은 59까지 입력할 수 있습니다.",
      });
      return;
    }

    // 총 분 계산 및 최소 5분 체크
    const totalMinutes = paymentDueDays * 1440 + paymentDueHours * 60 + paymentDueMinutesState;
    if (totalMinutes < 5) {
      setError("paymentDueMinutes", {
        type: "manual",
        message: "5분 이상의 시간을 입력해주세요. (최소값 5분)",
      });
      return;
    }

    // validation 통과하면 총 분을 form에 저장
    setValue("paymentDueMinutes", totalMinutes);
    clearErrors("paymentDueMinutes");
  }, [isEdit, paymentDueDays, paymentDueHours, paymentDueMinutesState, setError, clearErrors, setValue]);

  //  유효성 검사
  const formValidation = useCallback(() => {
    const requiredMessage = "필수입력 항목입니다";
    register("providerId", {
      required: { value: true, message: requiredMessage },
    });
    register("productType", {
      required: { value: true, message: requiredMessage },
    });
    register("productName", {
      required: { value: true, message: requiredMessage },
      maxLength: { value: 30, message: "30자 이상 입력 불가능 합니다." },
    });

    register("introduce", {
      required: { value: true, message: requiredMessage },
      maxLength: { value: 100, message: "30자 이상 입력 불가능 합니다." },
    });
    register("buildingList", {
      required: { value: true, message: requiredMessage },
    });
    // 내부 이용자 공개(일부 계약) 선택 시 노출 대상 계약 1건 이상 필수
    register("memberContractList", {
      validate: (value) => {
        if (getValues("status") !== ProductStatus.CONTRACT_ENABLED) return true;
        return (value?.length ?? 0) > 0 || "필수 선택 항목입니다";
      },
    });
  }, [register, getValues]);

  // 상태 변경 조건 검사 > 공개 여부 필드에서 호출
  const handleCheckProductStatus = async (value: ProductStatusT) => {
    if (value && value !== "DISABLED" && isEdit) {
      // 상태 변경조건
      try {
        await getProductStatus({ productId: +productId!, status: value });
      } catch (error) {
        const errorResponse = error instanceof AxiosError ? error.response : undefined;
        const data = errorResponse?.data;

        if (!data) {
          openErrorModal({ cause: error });
          return false;
        }

        if (data.meta.errorCode === "ePR0511") {
          try {
            const errorData = isObject(data.meta.errorData) ? data.meta.errorData : JSON.parse(data.meta.errorData);

            if (errorData.find((val: string) => val === "mediaList")) {
              setBaseModal({
                isOpen: true,
                title: `공간상품 / 사무공간 이미지를 등록해 주세요`,
                children: `최소 1장의 이미지를 등록해 주세요`,
                btnRightTitle: "확인",
              });

              return false;
            }

            if (errorData.find((val: string) => val === "isBuildingCommonFacilityToggleActive")) {
              setBaseModal({
                isOpen: true,
                title: `공용공간이 비활성화 상태입니다`,
                children: ` 공용공간을 사용하려면 각 항목의 토글을 활성화해 주세요`,
                btnRightTitle: "확인",
              });
              return false;
            }

            if (
              errorData.find((val: string) => val === "meetingRoomList") ||
              errorData.find((val: string) => val === "deskList") ||
              errorData.find((val: string) => val === "refreshRoomList")
            ) {
              let spaceTypeArr: string[] = [];
              errorData.forEach((element: string) => {
                if (element === "meetingRoomList") {
                  spaceTypeArr.push("회의실");
                }
                if (element === "deskList") {
                  spaceTypeArr.push("좌석");
                }
                if (element === "refreshRoomList") {
                  spaceTypeArr.push("편의시설");
                }
              });

              const spaceTypeArrText = spaceTypeArr.join("/");

              setBaseModal({
                isOpen: true,
                title: `${spaceTypeArrText}이 아직 등록되지 않았습니다`,
                children: `새로운 ${spaceTypeArrText}을 추가해 주세요`,
                btnRightTitle: "확인",
              });
              return false;
            }

            //필수 파라미터 누락
            setBaseModal({
              isOpen: true,
              title: `필수 항목을 입력해 주세요`,
              btnRightTitle: "확인",
              children: (
                <ul className="base-list">
                  {errorData.map((data: keyof typeof ProductErrorDataMap) => (
                    <li className="font14">{getErrorData(data)}</li>
                  ))}
                </ul>
              ),
            });
          } catch (error) {
            //필수 파라미터 누락
            setBaseModal({
              isOpen: true,
              title: `필수 항목을 입력해 주세요`,
              btnRightTitle: "확인",
            });
          }
        }

        if (data.meta.errorCode === "ePR0518") {
          //상품에 등록된 건물 중 활성화가 되지 않은 건물이 있을 경우
          setBaseModal({
            isOpen: true,
            title: `건물을 공개 상태로 변경해 주세요`,
            children: "건물 > 기본 정보를 확인해 주세요",
            btnRightTitle: "확인",
          });
        }

        if (data.meta.errorCode === "ePR0561") {
          setBaseModal({
            isOpen: true,
            title: `공용공간에 적용된 요금이 없습니다`,
            btnRightTitle: "확인",
            children: "공용공간 요금 정책 > 기본 요금을 등록해 주세요",
          });
        }

        if (data.meta.errorCode === "ePR0560") {
          setBaseModal({
            isOpen: true,
            title: `페이레터를 연동해 주세요`,
            btnRightTitle: "확인",
            children: "정산 정보 > 연동 정보를 확인해 주세요",
          });
        }

        if (data.meta.errorCode === "ePR0559") {
          setBaseModal({
            isOpen: true,
            title: `CS 담당자를 등록해 주세요`,
            btnRightTitle: "확인",
            children: "정산 정보 > 연락처 정보를 확인해 주세요",
          });
        }
        return false;
      }

      return true;
    }
  };

  // react hook form 에서 사용하는 validation rules, error message 정의
  useEffect(() => {
    formValidation();
  }, [formValidation, productDetail]);

  // validation 통과 후 submit 될때 실행
  const onSubmit = async (data: ProductFormData, e?: any) => {
    e.preventDefault();

    if (data.pgCodeOption?.items && data.pgCodeOption.items?.every((item) => !item.isEnabled)) {
      setIsPgcodeOptionCheckModal({
        isOpen: true,
        title: "결제 가능 수단을 1개 이상 선택해 주세요.",
      });
      return;
    }
    setSubmitData(data);

    setConfirmModal({ message: "저장하시겠습니까?", isOpen: true });
  };

  // 저장 / 수정
  const onClickConfirmModal = async (data: ProductFormData) => {
    // viewTable 데이터 -> 서버 productBuildingList 용으로 변환
    const productBuildingList = changeBuildings(data.buildingList);

    const basicInfoData: ProductAddModel | ProductEditModel = {
      productName: data.productName,
      introduce: data.introduce,
      providerId: data.providerId,
      productType: data.productType,
      isContractAvail: data.isContractAvail,

      ...(data.productType !== "DIGITAL_PRODUCT" && { productBuildingList: productBuildingList }),
      // 수정 시 필요한 데이터
      ...(isEdit && { id: productId }),
      ...(isEdit && { status: data.status }),
      ...(isEdit && { salePeriodType: data.salePeriodType }),
      ...(isEdit && { saleStartDate: data.saleStartDate }),
      ...(isEdit && { saleEndDate: data.saleEndDate }),
      ...(isEdit && { approvalType: data.approvalType }),
      ...(isEdit && { pgcodeOption: data.pgCodeOption }),
      ...(isEdit && { isMemberCancelable: data.isMemberCancelable }),
      ...(isEdit && data.isMemberCancelable && { memberCancelableMinutes: data.memberCancelableMinutes }),
      ...(isEdit && data.paymentDueMinutes && { paymentDueMinutes: data.paymentDueMinutes }),
      ...(isEdit && { isOnlinePayment: data.isOnlinePayment }),
    };

    const response = isEdit
      ? await updateProduct({ product: basicInfoData as unknown as ProductEditModel })
      : await createProduct({ product: basicInfoData });

    const result = response.data?.data?.content;
    if (result) {
      // 내부 이용자 공개(일부 계약) 대상 계약 매핑 저장 (RPP26)
      // 유지 계약은 isDeleted:false, 최초 매핑에 있었으나 해제된 계약은 isDeleted:true 로 전송
      if (isEdit && data.status === ProductStatus.CONTRACT_ENABLED) {
        const currentContracts = data.memberContractList ?? [];
        const currentIds = new Set(currentContracts.map((contract) => contract.contractManageId));
        const keptMaps = currentContracts.map((contract) => ({
          productId: Number(result),
          contractManageId: String(contract.contractManageId),
          contractId: String(contract.contractId),
          isDeleted: false,
        }));
        const deletedMaps = originalMemberContractsRef.current
          .filter((origin) => !currentIds.has(origin.contractManageId))
          .map((origin) => ({
            productId: Number(result),
            contractManageId: String(origin.contractManageId),
            contractId: String(origin.contractId),
            isDeleted: true,
          }));
        const internalMemberContractMap = [...keptMaps, ...deletedMaps];
        if (internalMemberContractMap.length > 0) {
          await postInternalMemberContractMaps({ productId: Number(result), internalMemberContractMap }).catch(() => undefined);
        }
      }

      openToast({
        content: `정상적으로 ${isEdit ? "수정" : "등록"}되었습니다.`,
      });
      navigate(PagePath.product.detail.replace(":id", `${result}`));
    }
  };

  const handleCopyClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(t_product_url);
      openToast({ content: "주소가 복사되었습니다" });
    } catch (err) {
      console.log(err);
      openToast({ content: "문제가 발생했습니다" });
    }
  };

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any) => {
    console.log("errors :>> ", errors);
  };

  return (
    <>
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          <form onSubmit={handleSubmit(onSubmit, onError)} id="productForm">
            <div className="contents-container__wrap-contents">
              <div className="contents-container__sub-title">
                <div className="minmax140 pb4">
                  <h2>기본정보</h2>
                </div>
              </div>

              {/* 프로바이더 */}
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">프로바이더</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="flex-row flex-center-start">
                    <BaseButton
                      title="선택"
                      className="color-white"
                      onClick={() =>
                        setAbstractModalZ1({
                          isOpen: true,

                          children: (
                            <SelectProviderPopup
                              defaultCheckedList={[selectedProvider?.providerId ?? ""]}
                              onClick={(checkedKeyList, checkedObjList) => {
                                setSelectedProvider({
                                  providerId: String(checkedObjList[0].providerId),
                                  uid: String(checkedObjList[0].providerId),
                                  providerName: String(checkedObjList[0].providerName),
                                });
                                fetchProviderDetail(String(checkedObjList[0].providerId));

                                setValue("providerId", String(checkedObjList[0].providerId), { shouldDirty: true });
                              }}
                              onClose={() => setAbstractModalZ1({ isOpen: false })}
                            />
                          ),
                        })
                      }
                    />
                    {selectedProvider?.providerName && (
                      <Link
                        to={PagePath.provider.detail.replace(":id", isEdit ? productDetail?.providerId! : selectedProvider?.providerId!)}
                        target="_blank"
                        className={"text-underline"}
                      >
                        <span className="ml16">{selectedProvider?.providerName}</span>
                        <span className="ic-target-blank"></span>
                      </Link>
                    )}

                    {errors.providerId?.message && <p className="validation-text">{errors.providerId?.message}</p>}
                  </div>
                </div>
              </section>

              {/* 상품 유형 */}
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">상품 유형</p>
                  {errors.productType?.message && <p className="validation-text">{errors.productType?.message}</p>}
                </div>
                <div className="contents-container__grid-contents">
                  <Controller
                    control={control}
                    name="productType"
                    render={({ field }) => {
                      return (
                        <div className="flex-row flex-center-start">
                          {objectKeys(ProductTypeLabels).map((value) => (
                            <BaseRadio
                              key={value}
                              id={value}
                              name={value}
                              label={ProductTypeLabels[value]}
                              onChange={(e) => {
                                if (e) {
                                  setValue("productType", value, { shouldDirty: true });
                                }
                              }}
                              checked={field.value === value}
                              value={value}
                              className="mr16"
                              disabled={
                                isEdit ||
                                value === "DIGITAL_PRODUCT" ||
                                value === "MAINTENANCE_FEE" ||
                                (value === "TIME_COURT" && isActiveTimeCourt === false)
                              }
                            />
                          ))}
                        </div>
                      );
                    }}
                  />
                </div>
              </section>
              {watch("productType") !== ProductTypes.DIGITAL_PRODUCT && (
                <>
                  {/* 건물/호실 */}
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">건물/호실</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <BaseButton title="선택" className="color-white" onClick={() => setBuildingModal({ isOpen: true })} />
                    </div>
                  </section>
                  {/*기본정보 페이지 내에 건물/호실 테이블 */}
                  <div className="contents-container__grid-contents">
                    <div>
                      <Controller
                        control={control}
                        name="buildingList"
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                          return (
                            <BuildingAndHoTable
                              viewTable={value || []}
                              setViewTable={onChange}
                              errorText={error?.message}
                              selectedRow={selectedRow}
                              setSelectedRow={setSelectedRow}
                              onSelectedRowsChange={setSelectedRow}
                              setBuildingAndHo={setBuildingAndHo}
                              isPrimaryBuildingChecked={isPrimaryBuildingChecked}
                              setValue={setValue}
                              getValues={getValues}
                            />
                          );
                        }}
                      ></Controller>
                    </div>
                  </div>
                </>
              )}

              <div className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">상품명</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <Controller
                      control={control}
                      name="productName"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                        return (
                          <BaseInput
                            className="mr8"
                            type="text"
                            onChange={onChange}
                            value={value}
                            name={name}
                            errorText={error?.message}
                            placeholder="등록하실 상품명을 30자 이내로 입력해 주세요."
                          />
                        );
                      }}
                    ></Controller>
                  </div>
                </div>
              </div>

              <div className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">1줄 소개</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax400">
                    <Controller
                      control={control}
                      name="introduce"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                        <BaseInput
                          className="mr8"
                          type="text"
                          onChange={onChange}
                          value={value}
                          name={name}
                          errorText={error?.message}
                          placeholder="등록하실 상품의 소개를 30자 이내로 입력해 주세요."
                        />
                      )}
                    ></Controller>
                  </div>
                </div>
              </div>

              {isEdit && (
                <>
                  {/* 공개 여부 */}
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <div className="flex-center">
                        <p className="required">공개 여부</p>
                        <BaseTooltip
                          touchIcon="QUESTION"
                          children={
                            <div>
                              TaapSpace 공개 여부를 설정합니다.
                              <ul className="base-list">
                                <li className="font13 text-primary">공개 안 함 : 누구에게도 공개하지 않습니다.</li>
                                <li className="font13 text-primary">
                                  내부 이용자 공개 (전체 계약) : 기존 공간임대 상품을 계약하여 이용 중인 모든 계약의 이용자에게 공개합니다.
                                </li>
                                <li className="font13 text-primary">
                                  내부 이용자 공개 (일부 계약) : 기존 공간임대 상품을 계약하여 이용 중인 일부 선택된 계약의 이용자에게만 공개합니다.
                                </li>
                                <li className="font13 text-primary">링크 공개 : 아래의 상품 링크로 접근 시에만 공개합니다.</li>
                                <li className="font13 text-primary">전체 공개 : 모든 사용자에게 제한 없이 공개합니다.</li>
                              </ul>
                            </div>
                          }
                        />
                      </div>
                      {errors.productType?.message && <p className="validation-text">{errors.productType?.message}</p>}
                    </div>
                    <div className="contents-container__grid-contents">
                      <Controller
                        control={control}
                        name="status"
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                          const memberContractCount = watch("memberContractList")?.length ?? 0;
                          // 공개 여부 변경. 내부 이용자 공개(일부 계약)가 아니면 대상 계약 필수 에러를 제거한다
                          const handleChangeStatus = (nextStatus: ProductStatusT) => {
                            onChange(nextStatus);
                            if (nextStatus !== ProductStatus.CONTRACT_ENABLED) clearErrors("memberContractList");
                          };
                          return (
                            <div className="flex-row flex-center-start">
                              {/* {objectKeys(ProductStatusLabels).map((status) => ( */}
                              {productStatusOptions
                                .filter((_, index) => index !== 0)
                                .map((option) => (
                                  <div key={option.value} className="flex-center-start mr16">
                                    <BaseRadio
                                      id={option.value}
                                      name={option.value}
                                      label={option.label}
                                      onChange={async (e) => {
                                        //공개 여부 변경 시 프로바이더 정산정보 유효성 체크
                                        //1. 신용카드 일반 경제 여부 true 체크
                                        //2. 현금 영수증 & 세금계산서 여부 true 체크
                                        //3. 계약 가능 여부 true 체크

                                        if (option.value !== ProductStatus.DISABLED) {
                                          if (getValues("productType") === "TIME_COURT" && getValues("isOnlinePayment") === true) {
                                            if (providerNormalCardApi?.isVerifiedPaymentKey !== true) {
                                              // 1. 신용카드 일반결제 검증
                                              setProductStatusModal({
                                                isOpen: true,
                                                message:
                                                  "‘정산정보 > 결제 연동 정보'에서 해당 프로바이더의 '신용카드 일반결제' 정보를 등록해 주세요.",
                                                title: "설정에 실패했습니다.",
                                              });
                                              return;
                                            }

                                            // 2. 현금영수증 검증
                                            if (providerPopbillApi?.isVerifiedCashbill !== true) {
                                              setProductStatusModal({
                                                isOpen: true,
                                                message: "‘정산정보 > 결제 연동 정보'에서 해당 프로바이더의 '현금영수증 API' 정보를 등록해 주세요.",
                                                title: "설정에 실패했습니다",
                                              });
                                              return;
                                            }

                                            // 3. 세금계산서 검증
                                            if (providerPopbillApi?.isVerifiedTaxInvoice !== true) {
                                              setProductStatusModal({
                                                isOpen: true,
                                                message: "‘정산정보 > 결제 연동 정보'에서 해당 프로바이더의 '세금계산서 API' 정보를 등록해 주세요.",
                                                title: "설정에 실패했습니다",
                                              });
                                              return;
                                            }
                                          }

                                          // 4. 계약 가능 여부 검증
                                          if (productDetail?.isContractAvail !== true) {
                                            setProductStatusModal({
                                              isOpen: true,
                                              message: "계약 가능 여부를 확인해 주세요.",
                                              title: "설정에 실패했습니다",
                                            });
                                            return;
                                          }

                                          // 모든 검증 통과 시 상태 업데이트
                                          const isValid = await handleCheckProductStatus(option.value as ProductStatusT);

                                          if (!isValid) return;
                                          handleChangeStatus(option.value as ProductStatusT);
                                          // setValue("status", status, { shouldDirty: true, shouldValidate: true });
                                        } else {
                                          handleChangeStatus(option.value as ProductStatusT);
                                          // setValue("status", status, { shouldDirty: true, shouldValidate: true });
                                        }
                                      }}
                                      checked={value === option.value}
                                      value={option.value}
                                      disabled={!isActiveProductPublic}
                                    />
                                    {/* 내부 이용자 공개 (일부 계약) 선택 시 계약 선택 UI */}
                                    {option.value === ProductStatus.CONTRACT_ENABLED && value === ProductStatus.CONTRACT_ENABLED && (
                                      <div className="flex-center-start" style={{ marginLeft: 8, gap: 4 }}>
                                        <button
                                          type="button"
                                          style={{
                                            background: "none",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            color: "#217BCE",
                                            textDecoration: "underline",
                                          }}
                                          onClick={() => setIsContractPopupOpen(true)}
                                        >
                                          {memberContractCount > 0 ? `선택 (${memberContractCount})` : "선택"}
                                        </button>
                                        {memberContractCount > 0 && (
                                          <button
                                            type="button"
                                            className="base-remove"
                                            aria-label="선택 계약 초기화"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              setValue("memberContractList", [], { shouldValidate: true });
                                            }}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          );
                        }}
                      />
                      {watch("status") === ProductStatus.CONTRACT_ENABLED && errors.memberContractList?.message && (
                        <p className="validation-text">{errors.memberContractList?.message}</p>
                      )}
                    </div>
                  </section>
                  {/* {watch("status") !== "DISABLED" && ( */}
                  <div className="bg-gray100 pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
                    <section className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p>판매 설정</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="flex-row flex-center-start">
                          {salePeriodTypeOptions
                            .filter((_, index) => index !== 0)
                            .map((option) => (
                              <Controller
                                control={control}
                                key={option.value}
                                name="salePeriodType"
                                render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                                  return (
                                    <BaseRadio
                                      id={option.value}
                                      name={option.value}
                                      label={option.label}
                                      onChange={(checked: boolean) => {
                                        checked && onChange(option.value);
                                      }}
                                      value={value}
                                      className="mr16"
                                      checked={value === option.value}
                                    />
                                  );
                                }}
                              />
                            ))}
                        </div>
                      </div>
                    </section>
                    <div className="flex-center">
                      <section className="contents-container__grid" style={{ width: 545 }}>
                        <div className="contents-container__grid-index">
                          <p>판매 시작일시</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div className="minmax400">
                            <Controller
                              control={control}
                              name="saleStartDate"
                              render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <>
                                  <FullDatePicker
                                    {...(watch("status") !== "DISABLED" &&
                                      value && {
                                        onClearClick: () => {
                                          onChange("");
                                        },
                                      })}
                                    disabled={watch("salePeriodType") !== "LIMITED"}
                                    selectedDate={value ? moment(value).toDate() : null}
                                    setDate={(selectedDate: Date) => {
                                      const date = moment(selectedDate).format(YmdFormat.WITH_TIME_ZONE);
                                      onChange(date);

                                      if (moment(watch("saleEndDate")).isSameOrBefore(date)) {
                                        setValue("saleEndDate", "");
                                      }
                                    }}
                                    className="mr10 minmax180"
                                    errorText={error?.message}
                                  />
                                </>
                              )}
                            ></Controller>
                          </div>
                        </div>
                      </section>

                      <section className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p>판매 종료일시</p>
                        </div>
                        <div className="contents-container__grid-contents ">
                          <div className="minmax400">
                            <Controller
                              control={control}
                              name="saleEndDate"
                              render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => (
                                <FullDatePicker
                                  {...(watch("status") !== "DISABLED" &&
                                    value && {
                                      onClearClick: () => {
                                        onChange("");
                                      },
                                    })}
                                  disabled={watch("salePeriodType") !== "LIMITED"}
                                  setDate={(selectedDate: Date) => {
                                    const startDate = moment(watch("saleStartDate"));
                                    let endDate = moment(selectedDate);

                                    // 선택된 종료일이 시작일보다 이전이면 시작일의 날짜로 설정
                                    if (endDate.isBefore(startDate, "day")) {
                                      endDate = moment(selectedDate).year(startDate.year()).month(startDate.month()).date(startDate.date());
                                    }

                                    // 시작일과 종료일이 같은 날짜이고, value가 없는 경우(처음 날짜 선택시)에만 시작시간 + 30분으로 설정
                                    if (endDate.isSame(startDate, "day") && !value) {
                                      const minTime = moment(startDate).add(30, "minutes");
                                      endDate = moment(selectedDate)
                                        .year(startDate.year())
                                        .month(startDate.month())
                                        .date(startDate.date())
                                        .hour(minTime.hour())
                                        .minute(minTime.minute());
                                    }

                                    const date = endDate.format(YmdFormat.WITH_TIME_ZONE);
                                    onChange(date);
                                  }}
                                  nonMarkedDate={true}
                                  minDate={moment(watch("saleStartDate")).toDate()}
                                  {...(watch("saleStartDate")
                                    ? {
                                        minTime: moment(value).isSame(moment(watch("saleStartDate")), "day")
                                          ? moment(watch("saleStartDate")).add(30, "minutes").toDate()
                                          : moment().startOf("day").toDate(),
                                        maxTime: moment().endOf("day").toDate(),
                                      }
                                    : {})}
                                  selectedDate={value ? moment(value).toDate() : null}
                                  className={`mr10 minmax180 `}
                                  errorText={error?.message}
                                />
                              )}
                            ></Controller>
                          </div>
                        </div>
                      </section>
                    </div>
                    <div className="contents-container__grid">
                      <div className="contents-container__grid-index">
                        <p className="">상품 링크</p>
                      </div>
                      <div className="contents-container__grid-contents">
                        <div className="minmax400">
                          {getValues("status") !== "DISABLED" ? (
                            <span className="font14 w-100 flex-center-start">
                              {t_product_url}
                              <span className="ml10 text-underline cursor-pointer" title="복사" onClick={handleCopyClipBoard}>
                                복사
                              </span>
                            </span>
                          ) : (
                            <p>-</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 승인 유형 > 공간예약 상품일 때만 노출 */}
                  {getValues("productType") === "TIME_COURT" && (
                    <>
                      <div className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p>승인 유형</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div>
                            <Controller
                              control={control}
                              name="approvalType"
                              render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                                return (
                                  <div className="flex-center">
                                    <BaseRadio
                                      id={"AUTO"}
                                      name={"AUTO"}
                                      label={"자동승인"}
                                      onChange={(checked: boolean) => {
                                        checked && onChange("AUTO");
                                      }}
                                      value={value}
                                      className="mr16"
                                      checked={value === "AUTO"}
                                    />

                                    <BaseRadio
                                      id="ADMIN"
                                      name="ADMIN"
                                      label="관리자승인"
                                      onChange={(checked: boolean) => {
                                        checked && onChange("ADMIN");
                                      }}
                                      value={value}
                                      className="mr16"
                                      checked={value === "ADMIN"}
                                    />
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p>이용 신청서</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div>
                            <span>{productDetail?.isUseApplication ? "사용" : "미사용"}</span>
                          </div>
                        </div>
                      </div>
                      <section className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p className="required">온라인 결제</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div className="minmax400">
                            <Controller
                              control={control}
                              name="isOnlinePayment"
                              render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                                return (
                                  <div className="flex-center">
                                    <BaseRadio
                                      id="isOnlinePayment-true"
                                      name="isOnlinePayment"
                                      label={"사용"}
                                      onChange={(checked: boolean) => {
                                        checked && onChange(true);
                                      }}
                                      className="mr16"
                                      checked={value === true}
                                    />

                                    <BaseRadio
                                      id="isOnlinePayment-false"
                                      name="isOnlinePayment"
                                      label="미사용"
                                      onChange={(checked: boolean) => {
                                        checked && onChange(false);
                                      }}
                                      className="mr16"
                                      checked={value === false}
                                    />
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>
                      </section>
                      <section className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p className="required">결제 가능 수단</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div className="minmax400">
                            <Controller
                              control={control}
                              name="pgCodeOption"
                              render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                                return (
                                  <div className="flex-center">
                                    {value?.items?.map((item) => (
                                      <BaseCheckbox
                                        key={item.pgcode}
                                        id={item.pgcode}
                                        name={item.pgcode}
                                        checked={item.isEnabled}
                                        className="mr16"
                                        label={changePgcodeLabel(item.pgcode)}
                                        disabled={watch("isOnlinePayment") === false}
                                        onChange={(checked: boolean) => {
                                          if (!checked) {
                                            const newItems = value.items.map((i) => (i.pgcode === item.pgcode ? { ...i, isEnabled: checked } : i));
                                            onChange({ items: newItems });
                                            return;
                                          }

                                          const newItems = value.items.map((i) => (i.pgcode === item.pgcode ? { ...i, isEnabled: checked } : i));
                                          onChange({ items: newItems });
                                        }}
                                      />
                                    ))}
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>
                      </section>
                      <div className="bg-gray100 pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
                        <section className="contents-container__grid mb0">
                          <div className="contents-container__grid-index">
                            <p className="">결제기한</p>
                          </div>
                          <div className="contents-container__grid-contents">
                            <div className="flex-center flex-row flex-center-start">
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedPaymentDueFieldRef.current = "days";
                                  setPaymentDueDays(Number(val) || 0);
                                }}
                                value={paymentDueDays}
                                placeholder="0"
                                maxLength={4}
                              />
                              <span className="ml8 mr16">일</span>
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedPaymentDueFieldRef.current = "hours";
                                  setPaymentDueHours(Number(val) || 0);
                                }}
                                value={paymentDueHours}
                                placeholder="0"
                                maxLength={2}
                              />
                              <span className="ml8 mr16">시간</span>
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedPaymentDueFieldRef.current = "minutes";
                                  setPaymentDueMinutesState(Number(val) || 0);
                                }}
                                value={paymentDueMinutesState}
                                placeholder="0"
                                maxLength={2}
                              />
                              <span className="ml8">분 이내</span>
                            </div>
                            {errors.paymentDueMinutes && <div className="validation-text mt5">{errors.paymentDueMinutes?.message}</div>}
                          </div>
                        </section>
                      </div>
                      <section className="contents-container__grid">
                        <div className="contents-container__grid-index">
                          <p className="">이용자 취소 가능 여부</p>
                        </div>
                        <div className="contents-container__grid-contents">
                          <div className="minmax400">
                            <Controller
                              control={control}
                              name="isMemberCancelable"
                              render={({ field: { onChange, value, name }, fieldState: { error } }) => {
                                return (
                                  <div className="flex-center">
                                    <BaseRadio
                                      id={"memberCancelable"}
                                      name={"isMemberCancelable"}
                                      label={"취소 가능"}
                                      onChange={(checked: boolean) => {
                                        checked && onChange(true);
                                      }}
                                      checked={value === true}
                                    />
                                    <BaseTooltip
                                      touchIcon={"QUESTION"}
                                      size={16}
                                      className="ml4 mr10"
                                      contents={`설정한 시간까지 이용자가 직접 취소 가능합니다.`}
                                    />
                                    <BaseRadio
                                      id="memberNonCancelable"
                                      name="isMemberCancelable"
                                      label="취소 불가"
                                      onChange={(checked: boolean) => {
                                        checked && onChange(false);
                                      }}
                                      checked={value === false}
                                    />
                                    <BaseTooltip
                                      touchIcon={"QUESTION"}
                                      size={16}
                                      className="ml4"
                                      contents={`계약체결(승인완료) 이후 이용자가 직접 취소 불가합니다.`}
                                    />
                                  </div>
                                );
                              }}
                            />
                          </div>
                        </div>
                      </section>

                      <div className="bg-gray100 pa20 mb10" style={{ borderRadius: "10px", width: 1070 }}>
                        <section className="contents-container__grid mb0">
                          <div className="contents-container__grid-index">
                            <p className="">이용시작일시</p>
                          </div>
                          <div className="contents-container__grid-contents">
                            <div className="flex-center flex-row flex-center-start">
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedFieldRef.current = "days";
                                  setOffsetDays(Number(val) || 0);
                                }}
                                value={offsetDays}
                                disabled={watch("isMemberCancelable") === false}
                                placeholder="0"
                                maxLength={4}
                              />
                              <span className="ml8 mr16">일</span>
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedFieldRef.current = "hours";
                                  setOffsetHours(Number(val) || 0);
                                }}
                                value={offsetHours}
                                disabled={watch("isMemberCancelable") === false}
                                placeholder="0"
                                maxLength={2}
                              />
                              <span className="ml8 mr16">시간</span>
                              <BaseInput
                                type="text"
                                className="minmax80"
                                onChange={(val: string) => {
                                  lastChangedFieldRef.current = "minutes";
                                  setOffsetMinutes(Number(val) || 0);
                                }}
                                value={offsetMinutes}
                                disabled={watch("isMemberCancelable") === false}
                                placeholder="0"
                                maxLength={2}
                              />
                              <span className="ml8">분 전까지 취소 가능</span>
                            </div>
                            {errors.memberCancelableMinutes && <div className="validation-text mt5">{errors.memberCancelableMinutes?.message}</div>}
                          </div>
                        </section>
                      </div>
                    </>
                  )}

                  {/* 계약 가능 여부 */}
                  <div className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p>계약 가능 여부</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax400">
                        <span>{productDetail?.isContractAvail ? "가능" : "불가능"}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </form>

          <>
            {/* 컨펌 모달 */}
            <BaseModal
              isOpen={confirmModal.isOpen}
              btnLeftTitle="취소"
              btnRightTitle="확인"
              onClose={() => setConfirmModal({ isOpen: false })}
              onClick={() => submitData && onClickConfirmModal(submitData)}
              title={confirmModal.message}
            ></BaseModal>
            {/* 공개 여부 모달 */}
            <BaseModal
              isOpen={productStatusModal.isOpen}
              btnRightTitle="확인"
              onClick={() => setProductStatusModal({ isOpen: false })}
              children={productStatusModal.message ? <p>{productStatusModal.message}</p> : null}
              title={productStatusModal.title}
            ></BaseModal>
            {/* 공개 여부 모달 */}
            {isPgcodeOptionCheckModal.isOpen && (
              <BaseModal
                isOpen={isPgcodeOptionCheckModal.isOpen}
                btnRightTitle="확인"
                onClick={() => setIsPgcodeOptionCheckModal({ isOpen: false })}
                children={isPgcodeOptionCheckModal.message ? <p>{isPgcodeOptionCheckModal.message}</p> : null}
                title={isPgcodeOptionCheckModal.title}
              ></BaseModal>
            )}

            {/*  빌딩 선택 팝업 */}
            {buildingModal.isOpen && (
              <ProductBuildingListModal
                setSelectedBuilding={setSelectedBuilding}
                partnerId={partnerId!}
                onClose={() => setCancelModal({ isOpen: true, message: "건물/호실 선택을 취소하시겠습니까?" })}
                onConfirm={() => {
                  if (selectedBuilding?.id) {
                    setFloorHoModal({ isOpen: true });
                  }
                }}
              />
            )}
            {/*건물의  층 / 호실 선택 팝업*/}
            {floorHoModal.isOpen && (
              <BuildingFloorHoModal
                selectedBuildingId={selectedBuilding?.id}
                floorHoTable={getValues("buildingList")} // was checked 확인
                selectOptions={(Propertys: any[]) => {
                  setFloorHoModal({ isOpen: false });
                  setBuildingModal({ isOpen: false });
                  onDrawViewTable(Propertys, setSelectedRow, setValue, selectedBuilding, setBuildingAndHo, buildingAndHo);
                }}
                onClose={() => {
                  setFloorHoModal({ isOpen: false });
                }}
              />
            )}
            {/* 내부 이용자 공개 (일부 계약) 계약 선택 팝업 */}
            {isContractPopupOpen && (
              <SearchContractPopup
                title="신청/계약"
                existContractManageIds={[]}
                defaultSelectedContracts={getValues("memberContractList") ?? []}
                contractStepFilter={[ContractStep.USE_PROGRESS, ContractStep.TERMINATE_RECEIVED]}
                spaceProductType="FULL_COURT"
                onClosed={() => setIsContractPopupOpen(false)}
                onClick={(contracts) => {
                  setValue("memberContractList", contracts, { shouldValidate: true });
                  setIsContractPopupOpen(false);
                }}
              />
            )}
            {/* 건물 선택 팝업 취소 */}
            {cancelModal.isOpen && (
              <BaseModal
                isOpen={true}
                btnLeftTitle="취소"
                btnRightTitle="확인"
                onClick={() => {
                  setBuildingModal({ isOpen: false });
                  setCancelModal({ isOpen: false });
                }}
                onClose={() => setCancelModal({ isOpen: false })}
                title={cancelModal.message}
              ></BaseModal>
            )}
            {/* 건물 대표여부 체크 경고 */}
            {alertModal.isOpen && (
              <BaseModal
                isOpen={true}
                btnRightTitle="확인"
                onClick={() => {
                  setAlertModal({ isOpen: false });
                }}
              >
                <div>
                  {alertModal.message?.split("\n").map((text) => (
                    <p key={text} className="mb8">
                      {text}
                    </p>
                  ))}
                </div>
              </BaseModal>
            )}
          </>
        </div>
      </div>
      {/* 버튼영역 */}
      <div className="contents-container__btn-wrap">
        <div className="left-area"></div>
        <div className="right-area">
          {
            <EditCancelButton
              to={
                isEdit
                  ? getProductDetailPath({
                      productId: productId ? String(productId) + "?tab=basicInfo" : "",
                    })
                  : PagePath.product.list
              }
            />
          }
          {isAuthority("w") && <BaseButton type="submit" title="저장" className="size-large" form="productForm" />}
        </div>
      </div>
    </>
  );
};

export default BasicInfoForm;
